import { useMemo, useState } from 'react'
import clsx from 'clsx'
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import type { ColumnDef, SortingState } from '@tanstack/react-table'
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/shadcn/table'
import { IconRightChevronOutline } from '@icons/IconRightChevronOutline'
import { SearchInput } from '../shared/SearchInput'
import {
  FileManagerCreateFolderDialog,
  FileManagerDeleteFileAction,
  FileManagerDownloadFileAction,
  FileManagerUploadDialog,
} from './FileManagerActions'
import {
  cloneTreeWithSorting,
  filterTreeBySearch,
  flattenTreeRows,
  formatContentType,
  formatCreatedAt,
  getDisplayName,
  getFileEmoji,
  getSortableValue,
  joinNamespace,
  transformLandingFilesToTree,
} from './fileManagerTreeUtils'
import type {
  FileManagerActionsComponents,
  FileManagerTreeTableRow,
  FileManagerTreeUploadRow,
  LandingFile,
} from './fileManagerTreeTypes'
import { cn } from '@/lib/utils'

const defaultFileManagerActions: FileManagerActionsComponents = {
  CreateFolderDialog: FileManagerCreateFolderDialog,
  DeleteFileAction: FileManagerDeleteFileAction,
  DownloadFileAction: FileManagerDownloadFileAction,
  UploadDialog: FileManagerUploadDialog,
}

function SortIcon({ direction }: { direction: false | 'asc' | 'desc' }) {
  if (direction === 'asc') {
    return <ArrowUp className="size-3.5" />
  }

  if (direction === 'desc') {
    return <ArrowDown className="size-3.5" />
  }

  return <ChevronsUpDown className="size-3.5 opacity-60" />
}

function ExpandableCellText({
  cellId,
  value,
  expandedCellIds,
  onToggle,
  className,
}: {
  cellId: string
  value: string
  expandedCellIds: Set<string>
  onToggle: (cellId: string) => void
  className?: string
}) {
  const isExpanded = expandedCellIds.has(cellId)

  return (
    <button
      type="button"
      className={clsx('block w-full text-left', !isExpanded && 'truncate', className)}
      title={!isExpanded ? value : undefined}
      onClick={(event) => {
        event.stopPropagation()
        onToggle(cellId)
      }}
    >
      {value}
    </button>
  )
}

interface FileManagerTreeTableProps {
  files: LandingFile[]
  namespace: string
  acceptedFormats?: `.${string}`[]
  maxFileSizeMb?: number
  actions?: FileManagerActionsComponents
  tableHeaderClassName?: string
  tableContainerClassName?: string
}

export function FileManagerTreeTable({
  files,
  namespace,
  acceptedFormats,
  maxFileSizeMb,
  actions = defaultFileManagerActions,
  tableHeaderClassName,
  tableContainerClassName,
}: FileManagerTreeTableProps) {
  const { CreateFolderDialog, DeleteFileAction, DownloadFileAction, UploadDialog } = actions
  const [sorting, setSorting] = useState<SortingState>([])
  const [expandedCellIds, setExpandedCellIds] = useState<Set<string>>(new Set())
  const [searchValue, setSearchValue] = useState('')
  const treeNodes = useMemo(() => transformLandingFilesToTree(files), [files])
  const filteredTreeNodes = useMemo(() => filterTreeBySearch(treeNodes, searchValue), [searchValue, treeNodes])
  const sortedTreeNodes = useMemo(() => cloneTreeWithSorting(filteredTreeNodes, sorting), [filteredTreeNodes, sorting])
  const [expandedFolderIds, setExpandedFolderIds] = useState<Set<string>>(() => new Set())

  const rows = useMemo<FileManagerTreeTableRow[]>(() => {
    const rootUploadRow: FileManagerTreeUploadRow = {
      nodeKind: 'upload-action',
      id: 'root:upload',
      depth: 0,
      folderPath: '',
      label: '...upload files',
    }

    return [rootUploadRow, ...flattenTreeRows(sortedTreeNodes, expandedFolderIds)]
  }, [expandedFolderIds, sortedTreeNodes])

  function toggleExpandedCell(cellId: string) {
    setExpandedCellIds((currentCellIds) => {
      const nextCellIds = new Set(currentCellIds)

      if (nextCellIds.has(cellId)) {
        nextCellIds.delete(cellId)
      } else {
        nextCellIds.add(cellId)
      }

      return nextCellIds
    })
  }

  const columns = useMemo<ColumnDef<FileManagerTreeTableRow>[]>(
    () => [
      {
        id: 'blobName',
        accessorFn: (row) => (row.nodeKind === 'upload-action' ? '' : getSortableValue(row, 'blobName')),
        header: 'Name',
        enableSorting: true,
        cell: ({ row }) => {
          const node = row.original
          if (node.nodeKind === 'upload-action') {
            return (
              <div style={{ paddingLeft: `${node.depth * 1.25 + 2.75}rem` }}>
                <UploadDialog
                  maxFileSizeMb={maxFileSizeMb}
                  uploadNamespace={joinNamespace(namespace, node.folderPath)}
                  refreshNamespace={namespace}
                  acceptedFormats={acceptedFormats}
                  triggerLabel={node.label}
                  triggerTooltip={node.label}
                  triggerClassName="h-7 rounded-md border border-border bg-background px-2 text-muted-foreground hover:text-foreground"
                />
              </div>
            )
          }

          const isFolder = node.nodeKind === 'folder'
          const isExpanded = isFolder && expandedFolderIds.has(node.id)
          const displayName = getDisplayName(node)

          return (
            <button
              type="button"
              className={clsx('flex w-full min-w-0 items-center gap-2 text-left', !isFolder && 'cursor-default')}
              style={{ paddingLeft: `${node.depth * 1.25}rem` }}
              title={isFolder ? node.path : (node.blobName ?? undefined)}
              onClick={() => {
                if (!isFolder) {
                  return
                }

                setExpandedFolderIds((currentFolderIds) => {
                  const nextFolderIds = new Set(currentFolderIds)

                  if (nextFolderIds.has(node.id)) {
                    nextFolderIds.delete(node.id)
                  } else {
                    nextFolderIds.add(node.id)
                  }

                  return nextFolderIds
                })
              }}
            >
              {isFolder ? (
                <>
                  <IconRightChevronOutline
                    className={clsx(
                      'size-4 shrink-0 text-muted-foreground transition-transform',
                      isExpanded && 'rotate-90'
                    )}
                  />
                  <span
                    className="flex size-7 shrink-0 items-center justify-center text-xl leading-none"
                    aria-hidden="true"
                  >
                    {isExpanded ? '📂' : '📁'}
                  </span>
                </>
              ) : (
                <>
                  <span className="size-4 shrink-0" />
                  <span
                    className="flex size-7 shrink-0 items-center justify-center text-xl leading-none"
                    aria-hidden="true"
                  >
                    {getFileEmoji(node)}
                  </span>
                </>
              )}
              <span
                className={clsx(
                  'min-w-0',
                  !expandedCellIds.has(`${node.id}:blobName`) && 'truncate',
                  isFolder && 'font-bold'
                )}
                onClick={(event) => {
                  if (isFolder) {
                    return
                  }

                  event.stopPropagation()
                  toggleExpandedCell(`${node.id}:blobName`)
                }}
              >
                {displayName}
              </span>
            </button>
          )
        },
      },
      {
        accessorKey: 'contentType',
        header: 'Content type',
        enableSorting: true,
        cell: ({ row }) => {
          if (row.original.nodeKind === 'folder' || row.original.nodeKind === 'upload-action') {
            return <span className="text-muted-foreground">-</span>
          }

          return (
            <ExpandableCellText
              cellId={`${row.original.id}:contentType`}
              value={formatContentType(row.original)}
              expandedCellIds={expandedCellIds}
              onToggle={toggleExpandedCell}
            />
          )
        },
      },
      {
        accessorKey: 'createdBy',
        header: 'Created by',
        enableSorting: true,
        cell: ({ row }) => {
          if (row.original.nodeKind === 'folder' || row.original.nodeKind === 'upload-action') {
            return <span className="text-muted-foreground">-</span>
          }

          return (
            <ExpandableCellText
              cellId={`${row.original.id}:createdBy`}
              value={row.original.createdBy || '-'}
              expandedCellIds={expandedCellIds}
              onToggle={toggleExpandedCell}
            />
          )
        },
      },
      {
        accessorKey: 'createdAt',
        header: 'Created at',
        enableSorting: true,
        cell: ({ row }) => {
          if (row.original.nodeKind === 'folder' || row.original.nodeKind === 'upload-action') {
            return <span className="text-muted-foreground">-</span>
          }

          return (
            <ExpandableCellText
              cellId={`${row.original.id}:createdAt`}
              value={formatCreatedAt(row.original.createdAt)}
              expandedCellIds={expandedCellIds}
              onToggle={toggleExpandedCell}
              className="whitespace-nowrap"
            />
          )
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }) => {
          if (row.original.nodeKind === 'folder') {
            const newSubfolderAction = (
              <CreateFolderDialog
                parentNamespace={joinNamespace(namespace, row.original.path)}
                refreshNamespace={namespace}
                triggerTooltip="New subfolder"
                triggerAriaLabel="New subfolder"
                triggerIconClassName="size-5"
              />
            )

            if (row.original.children.length === 0 && row.original.placeholderFile) {
              return (
                <div className="flex justify-end gap-1">
                  {newSubfolderAction}
                  <DeleteFileAction
                    file={row.original.placeholderFile}
                    namespace={namespace}
                    deleteNamespace={joinNamespace(
                      row.original.placeholderFile.namespace ?? namespace,
                      row.original.path
                    )}
                    description={`Are you sure you want to delete the empty folder ${row.original.name}?`}
                    successMessage="Folder deleted successfully"
                    errorMessage="Failed to delete folder"
                  />
                </div>
              )
            }

            return <div className="flex justify-end gap-1">{newSubfolderAction}</div>
          }

          if (row.original.nodeKind === 'upload-action') {
            return null
          }

          return (
            <div className="flex justify-end gap-1">
              <DownloadFileAction file={row.original} />
              <DeleteFileAction
                file={row.original}
                namespace={namespace}
                deleteNamespace={joinNamespace(namespace, row.original.folderPath)}
              />
            </div>
          )
        },
        size: 80,
      },
    ],
    [
      CreateFolderDialog,
      DeleteFileAction,
      DownloadFileAction,
      UploadDialog,
      acceptedFormats,
      expandedCellIds,
      expandedFolderIds,
      maxFileSizeMb,
      namespace,
    ]
  )

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
    onSortingChange: setSorting,
    state: {
      sorting,
    },
  })

  return (
    <div className="space-y-3">
      <div className="flex justify-end gap-2">
        <CreateFolderDialog parentNamespace={namespace} refreshNamespace={namespace} />
        <UploadDialog
          maxFileSizeMb={maxFileSizeMb}
          uploadNamespace={namespace}
          refreshNamespace={namespace}
          acceptedFormats={acceptedFormats}
        />
        <SearchInput value={searchValue} onChange={setSearchValue} placeholder="Search files..." className="w-72" />
      </div>
      <div className={cn('overflow-hidden bg-background', tableContainerClassName)}>
        <Table className="table-fixed">
          <TableHeader className={cn('bg-offwhite [&_tr]:border-0', tableHeaderClassName)}>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="border-0 hover:bg-transparent">
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id} className="border-0 font-bold">
                    {header.isPlaceholder ? null : header.column.getCanSort() ? (
                      <button
                        type="button"
                        className="flex w-full items-center gap-2 text-left"
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        <SortIcon direction={header.column.getIsSorted()} />
                      </button>
                    ) : (
                      flexRender(header.column.columnDef.header, header.getContext())
                    )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody className="[&_tr:last-child]:border-0">
            {table.getRowModel().rows.length > 0 ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  className={clsx(
                    'border-0 animate-in fade-in-0 slide-in-from-top-1 hover:bg-primary/5',
                    row.original.nodeKind === 'upload-action' && 'bg-muted/40 hover:bg-muted/60'
                  )}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className={clsx('border-0 py-0.5', row.original.nodeKind === 'upload-action' && 'py-2')}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center text-muted-foreground">
                  No files to show.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
